import type { TicketListItem } from '../types';
import { UserAvatar } from './UserAvatar';
import { supportName } from '../lib/people';
import { ClientLogo } from './ClientLogo';
import { rowTone } from '../lib/rowTone';
import { useAuth } from '../context/AuthContext';
import { parseRowColor } from '../lib/preferences';
import { PrioEtatCase } from './PrioEtatCase';

const TYPE_ICON: Record<string, { c: string; bg: string; fg: string }> = {
  Bug: { c: '!', bg: 'var(--danger-soft)', fg: '#dc2626' },
  Evolution: { c: '+', bg: 'var(--success-soft)', fg: '#059669' },
  Question: { c: '?', bg: 'var(--warning-soft)', fg: '#d97706' },
  DP: { c: 'D', bg: '#eff6ff', fg: '#1d4ed8' },
};

function formatTemps(decimalHours: number | null): string {
  if (!decimalHours || decimalHours === 0) return '0 H 00';
  const h = Math.floor(decimalHours);
  const m = Math.round((decimalHours - h) * 60);
  return `${h} H ${m.toString().padStart(2, '0')}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

/**
 * Ligne d'un point. Fond de toute la ligne selon la règle du V1 (lib/rowTone),
 * et `alt` une ligne sur deux pour l'alternance (index.css, .trow.alt).
 */
export function TicketRow({ t, alt = false }: { t: TicketListItem; alt?: boolean }) {
  const ic = TYPE_ICON[t.type_tache] ?? TYPE_ICON['Bug']!;
  const tps = (Number(t.total_temps) || 0) + (Number(t.total_decredit) || 0) / 60;
  const evalH = Number(t.eval_temps) || 0;
  const tone = rowTone(t.priorite, t.etat);
  // Préférence du compte (Profil) : couleur V1 sur la case « État » seulement
  // (défaut, retour de Julien : la ligne entière fatigue les yeux) ou sur
  // toute la ligne.
  const { user } = useAuth();
  const wholeRow = parseRowColor(user?.preferences?.row_color) === 'ligne';

  return (
    <div className="trow-c">
      <div className={`trow${alt ? ' alt' : ''}`} data-tone={wholeRow ? (tone ?? undefined) : undefined}>
        <code className="trow-id" style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
          #{t.display_id}
        </code>
        <span
          title={t.type_tache}
          className="trow-type trow-type-icon"
          style={{ background: ic.bg, color: ic.fg }}
        >
          {ic.c}
        </span>
        {/* Une seule case, comme le V1 : priorité en gras puis état en entier,
            centrés, remplie de la couleur V1 (réglage « Case état seulement »). */}
        <PrioEtatCase priorite={t.priorite} etat={t.etat} colored={!wholeRow} />
        <div className="trow-date">{formatDate(t.date_creation)}</div>
        <div className="trow-logo">
          <ClientLogo id={t.id_client} name={t.client_nom} size={30} />
        </div>
        <div className="trow-client">{t.client_nom ?? '–'}</div>
        <div className="trow-subject">
          <div className="trow-title">
            {t.hors_tma && <span className="badge-hors-tma">Hors TMA</span>}
            {t.nom_tache}
          </div>
          <div className="trow-meta">
            Tps {formatTemps(tps)} · Eval {formatTemps(evalH)}
          </div>
        </div>
        <div className="trow-people">
          {t.support_ids.slice(0, 3).map((id) => (
            <UserAvatar
              key={id}
              id={id}
              size={30}
              name={supportName(t.support_users, id)}
            />
          ))}
          {t.support_ids.length > 3 && (
            <span className="trow-more" title={`+${t.support_ids.length - 3} autre(s)`}>
              +{t.support_ids.length - 3}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
